import { Badge, ButtonPrimitive, Text } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { BillingCycle } from '@/types/shared';
import { billingCycleToggleStyles as styles } from './BillingCycleToggle.stylex';

interface BillingCycleToggleProps {
  annualDiscountLabel: string;
  isAnnualDiscount30Enabled: boolean;
  onChange: (billingCycle: BillingCycle) => void;
  value: BillingCycle;
}

export function BillingCycleToggle({
  annualDiscountLabel,
  isAnnualDiscount30Enabled,
  onChange,
  value,
}: BillingCycleToggleProps) {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.ARIA)}
      {...stylex.props(styles.toggleGroup)}
    >
      {[BillingCycle.Monthly, BillingCycle.Yearly].map((billingCycle) => {
        const isSelected = value === billingCycle;
        const isYearly = billingCycle === BillingCycle.Yearly;
        const label = isYearly
          ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY)
          : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.MONTHLY);

        return (
          <ButtonPrimitive
            key={billingCycle}
            aria-pressed={isSelected}
            onClick={() => onChange(billingCycle)}
            {...stylex.props(
              styles.toggleOption,
              isYearly && styles.annualToggleOption,
              isSelected && styles.toggleOptionSelected,
            )}
          >
            <Text.span variant={isSelected ? 'body-medium-strong' : 'body-medium'}>{label}</Text.span>
            {isYearly && (
              <span
                {...stylex.props(
                  styles.discountBadge,
                  isAnnualDiscount30Enabled && isSelected && styles.annualDiscountBadge,
                  isAnnualDiscount30Enabled && !isSelected && styles.inactiveDiscountBadge,
                )}
              >
                <Badge
                  variant={isAnnualDiscount30Enabled ? (isSelected ? 'brandFilled' : 'defaultFilled') : 'brandOutline'}
                  size={isAnnualDiscount30Enabled ? 'lg' : 'md'}
                >
                  {annualDiscountLabel}
                </Badge>
              </span>
            )}
          </ButtonPrimitive>
        );
      })}
    </div>
  );
}
